import { ComponentProps, useEffect, useMemo, useState } from 'react';
import { MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ColumnType } from 'src/components/molecules';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import {
  AppliedFiltersType,
  Exact,
  FuzzySearchInputType,
  InputMaybe,
  OrderByInputType,
  PaginationInputType,
  SearchByInputType,
} from 'src/graphql';
import { FilterArray, setSearchFilter } from 'src/utils';

type PaginatedQueryVariables = Exact<{
  pagination: PaginationInputType;
  orderBy: OrderByInputType;
  searchBy: SearchByInputType | SearchByInputType[];
  fuzzy?: InputMaybe<FuzzySearchInputType>;
  appliedFilters: AppliedFiltersType;
}>;

export type SearchValueProps = {
  label: string;
  value: string;
  __isNew__: boolean;
}[];

export type DefaultSearchOptionsType = {
  label: string;
  value: string;
  field?: string;
};

export const useTableColumns = (
  rows: number,
  page: number,
  columns: ColumnType[],
  defaultHiddenColumns: string[],
  defaultSortedColumn: string,
  defaultSortedDirection: 'asc' | 'desc',
  searchOptions: DefaultSearchOptionsType[],
  appliedFilters: AppliedFiltersType = {},
) => {
  const defaultVisibleColumns = columns.filter(
    (c) => !defaultHiddenColumns?.includes(c.label),
  );
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const [sortingDetails, setSortingDetails] = useState<
    [string, 'asc' | 'desc']
  >([defaultSortedColumn, defaultSortedDirection]);
  const [fieldName, direction] = sortingDetails;
  const [searchBy, setSearchBy] = useState<SearchByInputType[]>([]);
  const [fuzzy, setFuzzy] = useState<FuzzySearchInputType | null>(null);
  const [searchValue, setSearchValue] =
    useState<ComponentProps<typeof CreatableSelect>['value']>();

  const sortColumns = (columnName: string) => {
    const column = columns.find((c) => c.label === columnName);
    const oppositeDirection = direction === 'asc' ? 'desc' : 'asc';
    const newSortDirection =
      fieldName === column?.value ? oppositeDirection : 'asc';
    setSortingDetails([column?.value?.toString() ?? '', newSortDirection]);
  };

  const sortedColumn = useMemo(
    () => ({
      columnName:
        columns.find((c) => c.value === fieldName)?.label ??
        defaultSortedColumn,
      direction,
    }),
    [columns, fieldName, direction, defaultSortedColumn],
  );

  const variables = useMemo(
    (): PaginatedQueryVariables => ({
      orderBy: { fieldName, direction },
      pagination: {
        limit: Number(rows),
        offset: (Number(page) - 1) * Number(rows),
      },
      searchBy,
      fuzzy,
      appliedFilters,
    }),
    [rows, page, fieldName, direction, searchBy, fuzzy, appliedFilters],
  );

  const stableSearchOptions = useMemo(
    () => searchOptions,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(searchOptions)],
  );

  useEffect(() => {
    const { fieldFilters, fuzzyFilter } = setSearchFilter(
      stableSearchOptions,
      searchValue as FilterArray,
    );
    setSearchBy(fieldFilters);
    setFuzzy(fuzzyFilter);
  }, [searchValue, stableSearchOptions]);

  const search = (value: SearchValueProps) => setSearchValue(value);

  const updateVisibleColumns = (selectedOptions: MultiValue<DefaultOption>) => {
    if (selectedOptions === null) {
      setVisibleColumns([]);
      return;
    }
    if (Array.isArray(selectedOptions)) {
      const columnValues = columns.map((c) => c.value);
      const newColumns = selectedOptions.sort(
        (a, b) => columnValues.indexOf(a.value) - columnValues.indexOf(b.value),
      );
      setVisibleColumns(newColumns);
    }
  };

  return {
    visibleColumns,
    sortedColumn,
    sortColumns,
    variables,
    search,
    updateVisibleColumns,
    searchValue,
  };
};
